import Service from '@ember/service';
import { getLoggerNameForEmberObject } from 'sh-www/utils/misc';
import {
  withScope,
  captureException as sentryCaptureException,
  captureMessage as sentryCaptureMessage
} from '@sentry/ember';

const SEVERITY_ERROR = 'error';
const SEVERITY_WARNING = 'warning';
const SEVERITY_INFO = 'info';

const getLogger = (emberObject) => {
  return emberObject ? getLoggerNameForEmberObject(emberObject) : 'javascript';
};

/**
 * This service should be used to track error through our external error tracking client raven.js
 */
class ShErrorService extends Service {
  /**
   * Track an Error or a message at the 'error' level.  This will output to console and track to our third party.
   *
   * @param {Error|String} errorToTrack
   * @param {Ember.Object=} opt_emberObject   Any type of Ember object (Service, Component, etc)
   * @param {Error=} opt_error                You only would need to pass in this object if you did NOT pass in an Error
   *                                          for errorToTrack.  This will allow a string message + error stringified.
   */
  error(errorToTrack, opt_emberObject, opt_error) {
    const logger = getLogger(opt_emberObject);

    if (opt_error) {
      console.error('%o -- Ember Object -- %o ERROR %o', errorToTrack, logger, opt_error);
    } else {
      console.error('%o -- Ember Object -- %o', errorToTrack, logger);
    }

    if (errorToTrack instanceof Error) {
      this.captureException(errorToTrack, SEVERITY_ERROR, logger);
      return;
    }

    if (opt_error) {
      let stringifyText = 'unknown-stringify';

      try {
        stringifyText = JSON.stringify(opt_error);
      } catch (e) {
        // gobble gobble -- just send unknown
      }

      errorToTrack += ` -- ERROR -- ${stringifyText}`;
    }

    this.captureMessage(errorToTrack, SEVERITY_ERROR, logger);
  }

  /**
   * Track an Error or a message at the 'warn' level.  This will output to console and track to our third party.
   *
   * @param {Error|String} errorToTrack
   * @param {Ember.Object=} opt_emberObject   Any type of Ember object (Service, Component, etc)
   * @param {Error=} opt_error                You only would need to pass in this object if you did NOT pass in an Error
   *                                          for errorToTrack.  This will allow a string message + error stringified.
   */
  warn(errorToTrack, opt_emberObject, opt_error) {
    const logger = getLogger(opt_emberObject);

    if (opt_error) {
      console.warn('%o -- Ember Object -- %o WARN %o', errorToTrack, logger, opt_error);
    } else {
      console.warn('%o -- Ember Object -- %o', errorToTrack, logger);
    }

    if (errorToTrack instanceof Error) {
      this.captureException(errorToTrack, SEVERITY_WARNING, logger);
      return;
    }

    if (opt_error) {
      errorToTrack += ` -- WARN -- ${JSON.stringify(opt_error)}`;
    }

    this.captureMessage(errorToTrack, SEVERITY_WARNING, logger);
  }

  /**
   * Track an Error or a message at the 'info' level.  This will output to console and track to our third party.
   *
   * @param {Error|String} errorToTrack
   * @param {Ember.Object} opt_emberObject    Any type of Ember object (Service, Component, etc)
   */
  info(errorToTrack, opt_emberObject) {
    const logger = getLogger(opt_emberObject);

    if (errorToTrack instanceof Error) {
      this.captureException(errorToTrack, SEVERITY_INFO, logger);
      return;
    }

    this.captureMessage(errorToTrack, SEVERITY_INFO, logger);
  }

  /**
   * Internal call to the third party client for an Error
   *
   * @param {Error} error
   * @param {Sentry.Severity} level    of type LOGGER_LEVEL_*
   * @private
   */
  captureException(error, level) {
    withScope((scope) => {
      scope.setLevel(level);
      sentryCaptureException(error);
    });
  }

  /**
   * Internal call to the third party client for a string message
   *
   * @param {string} message
   * @param {Sentry.Severity} level    of type LOGGER_LEVEL_*
   * @private
   */
  captureMessage(message, level) {
    sentryCaptureMessage(message, level);
  }
}

export default ShErrorService;
